export type TermsSection = {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
};

export const TERMS_OF_USE_CONTENT = {
  title: "VEVI Monster Terms of Use",
  lastUpdated: "Last updated: June 5, 2026",
  seoTitle: "Terms of Use | VEVI Monster",
  seoDescription:
    "Review the terms governing VEVI Monster AI dubbing, captions, audio translation, uploads, payments, and permitted use.",
  backToHomeLabel: "Back to Home",
  companyName: "General Secret OÜ",
  companyDetails:
    "Registry code 17112803, Vesivärava tn 50-201, Kesklinna linnaosa, Tallinn, Harju maakond, 10152, Estonia",
  email: "site@vevi.monster",
  sections: [
    {
      title: "1. Acceptance of These Terms",
      paragraphs: [
        "These Terms of Use govern your access to and use of vevi.monster and the related VEVI Monster services, including AI dubbing, captions, lip sync, audio translation, and localization workflows.",
        "By using the website, uploading media, starting processing, making a payment, or receiving outputs from the service, you agree to these Terms.",
      ],
    },
    {
      title: "2. Eligibility and Access",
      paragraphs: [
        "You must use the service only in compliance with applicable law and only if you have the legal right and authority to do so.",
        "Some VEVI Monster features may work without a formal account, but you may be required to provide contact details such as an email address to receive service updates, files, or delivery notifications.",
      ],
    },
    {
      title: "3. Allowed Use and Prohibited Conduct",
      bullets: [
        "You may use VEVI Monster only for lawful purposes.",
        "You may not upload, process, publish, or distribute content that is illegal, infringing, deceptive, harmful, hateful, abusive, or violates another person's rights.",
        "You may not use the service to impersonate others, misappropriate voice or likeness rights, or process content without required permissions.",
        "You may not interfere with, reverse engineer, abuse, or disrupt the website, upload flows, checkout flows, or backend processing systems.",
      ],
    },
    {
      title: "4. Your Content and Permissions",
      paragraphs: [
        "You retain ownership of the video, audio, text, and other materials you upload to VEVI Monster.",
        "You represent and warrant that you own or control all rights necessary to upload, translate, dub, caption, synchronize, modify, and distribute that content, including rights related to copyright, voice, image, likeness, privacy, publicity, and trademarks.",
        "You grant VEVI Monster a limited, non-exclusive, worldwide license to host, store, process, reproduce, modify, and transmit your content solely for the purpose of operating the service, generating requested outputs, troubleshooting issues, and fulfilling support or legal obligations.",
      ],
    },
    {
      title: "5. Payments, Pricing, and Refunds",
      paragraphs: [
        "VEVI Monster primarily operates on a pay-per-use basis unless a separate pricing arrangement is clearly offered on a specific page or checkout flow.",
        "All pricing, fees, and service details are shown at the time you initiate processing or payment. Payments are processed by Revolut and related payment providers.",
        "You agree to provide accurate billing and contact information and to pay all charges associated with your use of the service.",
        "If you need help with a charge or want to request a refund, contact us through site chat or at site@vevi.monster. Where the service copy states that refunds are available within 24 hours after payment, those requests must be made within that time window and may be reviewed together with any mandatory consumer rights that apply.",
      ],
    },
    {
      title: "6. AI Outputs and Service Limitations",
      paragraphs: [
        "VEVI Monster uses automated systems to generate translated voices, dubbing, captions, lip sync, and related outputs. Because these outputs are generated with AI and third-party infrastructure, they may vary in quality, timing, wording, pronunciation, synchronization, or completeness.",
        "You are responsible for reviewing outputs before publishing, distributing, or relying on them. VEVI Monster does not guarantee that every result will be error-free, uninterrupted, or fit for a particular purpose.",
      ],
    },
    {
      title: "7. Ownership of Outputs",
      paragraphs: [
        "Subject to your full payment of any required fees, you may use the outputs generated for your order in your own content and business workflows.",
        "You remain responsible for ensuring that your use of those outputs is lawful and that your underlying source content gave you the right to create and exploit them.",
        "VEVI Monster retains all rights in the website, branding, product design, software, workflows, and other platform materials that are not part of your uploaded content or paid deliverables.",
      ],
    },
    {
      title: "8. Suspension and Termination",
      paragraphs: [
        "We may suspend, limit, or terminate access to VEVI Monster if we reasonably believe that you violated these Terms, created legal or security risk, abused the service, or interfered with other users or infrastructure.",
        "We may also refuse or cancel processing where content appears unlawful, infringing, unsafe, technically incompatible, or otherwise outside the intended use of the service.",
      ],
    },
    {
      title: "9. Third-Party Services",
      paragraphs: [
        "VEVI Monster relies on third-party services for analytics, payments, hosting, storage, communications, and processing infrastructure. Those services may have their own terms, privacy notices, and technical limitations.",
        "We are not responsible for third-party websites, payment environments, or platforms that you access outside vevi.monster.",
      ],
    },
    {
      title: "10. Disclaimer of Warranties",
      paragraphs: [
        "To the fullest extent permitted by law, VEVI Monster is provided on an \"as is\" and \"as available\" basis without warranties of any kind, whether express or implied.",
      ],
    },
    {
      title: "11. Limitation of Liability",
      paragraphs: [
        "To the fullest extent permitted by law, VEVI Monster and its affiliates, officers, employees, contractors, and service providers will not be liable for indirect, incidental, special, consequential, exemplary, or lost-profit damages arising out of or related to your use of the service.",
        "Nothing in these Terms limits liability where such limitation is prohibited by applicable law.",
      ],
    },
    {
      title: "12. Indemnification",
      paragraphs: [
        "You agree to defend, indemnify, and hold harmless VEVI Monster, its affiliates, and service providers from claims, damages, liabilities, costs, and expenses arising from your content, your use of the service, your violation of these Terms, or your violation of any third-party rights.",
      ],
    },
    {
      title: "13. Privacy",
      paragraphs: [
        "Your use of VEVI Monster is also governed by our Privacy Policy, which explains how we collect, use, and protect personal data.",
      ],
    },
    {
      title: "14. Governing Law",
      paragraphs: [
        "These Terms are governed by the laws of Estonia, without regard to conflict-of-law rules, except where mandatory consumer protection law requires otherwise.",
      ],
    },
    {
      title: "15. Changes to These Terms",
      paragraphs: [
        "We may update these Terms from time to time to reflect product, operational, or legal changes. When we do, we will update the revision date on this page.",
      ],
    },
    {
      title: "16. Contact",
      paragraphs: [
        "If you have questions about these Terms, contact us at site@vevi.monster.",
      ],
    },
  ] satisfies TermsSection[],
} as const;
